import { Dispatch, Fragment, SetStateAction, useRef, useState } from 'react'
import {
  Box,
  Flex,
  Avatar,
  Text,
  Divider,
  Stack,
  useColorModeValue,
  Menu,
  MenuList,
  MenuButton,
} from '@chakra-ui/react'

import { User } from 'UnifiedLogin/types'
import { MessageCenterMessage } from '../../types'
import MessageCenterDeleteMessage from './components/MessageCenterDeleteMessage'
import MessageCenterUpdateMessage from './components/MessageCenterUpdateMessage'
import MessageCenterCopyMessage from './components/MessageCenterCopyMessage'
import { useMessageCenterChatManager } from '../../contexts/MessageCenterChatManager/Provider'
import { capitalizeString, getFormattedAvatarUrl } from 'utils/transforms/string'

type MenuPosition = {
  x: number
  y: number
}

export type MessageCenterMessageCardActionsProps = {
  message: MessageCenterMessage
  setMenuPosition: Dispatch<SetStateAction<MenuPosition | null>>
}

type Props = {
  currentUser: User
  message: MessageCenterMessage
  index: number
}

export default function MessageCenterMessageCard({ currentUser, message, index }: Props) {
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)

  const boxRef = useRef<HTMLDivElement | null>(null)

  const openMenu = (x: number, y: number) => {
    setMenuPosition({ x, y })
  }

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    openMenu(event.clientX, event.clientY)
  }

  const handleOnClick = () => {
    if (boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect()
      openMenu(rect.left + 4, rect.bottom - 8)
    }
  }

  const handleClose = () => setMenuPosition(null)

  const currentUserBubbleBg = useColorModeValue('blue.400', 'blue.600')
  const currentUserColor = useColorModeValue('white', 'black')
  const defaultBubbleBg = useColorModeValue('gray.200', 'gray.700')
  const defaultBubbleColor = useColorModeValue('black', 'white')

  const { routes, messages } = useMessageCenterChatManager()

  const checkMessageTimeToUpdate = () => {
    const inputDate = new Date(message.created_at as string)
    const now = new Date()
    const diffMs = now.getTime() - inputDate.getTime()
    return diffMs >= 3600000
  }

  const formatDate = (iso?: string) => {
    if (!iso) return ''
    const date = new Date(iso)
    return date.toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const isCurrent = message.person_id === currentUser?.id

  const previosMessage = messages[index - 1]
  const showAvatar = !isCurrent && (!previosMessage || previosMessage.person_id !== message.person_id)

  const bubbleBg = isCurrent ? currentUserBubbleBg : defaultBubbleBg
  const bubbleColor = isCurrent ? currentUserColor : defaultBubbleColor

  const currentDate = formatDate(message?.created_at)
  const previousMessageDate = previosMessage ? formatDate(previosMessage.created_at) : null
  const showDateDivider = !previosMessage || currentDate !== previousMessageDate

  return (
    <Fragment>
      {showDateDivider && (
        <Flex align="center" my={4}>
          <Divider flex="1" borderColor="gray.400" />
          <Text mx={2} fontSize="sm" color="gray.500">
            {currentDate}
          </Text>
          <Divider flex="1" borderColor="gray.400" />
        </Flex>
      )}

      <Flex my={2} justify={isCurrent ? 'flex-end' : 'flex-start'}>
        {!isCurrent && showAvatar && (
          <Avatar size="sm" src={getFormattedAvatarUrl(message, 'person_main')} name={message.person_name} mr={3} />
        )}

        <Box maxW={{ base: '80%', md: '70%' }} ml={!showAvatar ? '44px' : '0px'}>
          {!isCurrent && showAvatar && (
            <Text fontSize="sm" mb={1}>
              {capitalizeString(message.person_name)}
            </Text>
          )}

          <Box
            ref={boxRef}
            className="accessible"
            bg={bubbleBg}
            color={bubbleColor}
            p={2}
            px={4}
            borderRadius="md"
            boxShadow="sm"
            textAlign="left"
            onContextMenu={handleContextMenu}
            onClick={handleOnClick}
          >
            <Flex align="center" gap={4}>
              <Stack spacing={0}>
                <Text whiteSpace="pre-wrap">{message.message}</Text>
                <Text fontSize="xs" opacity={0.6} textAlign="right" mt={1}>
                  {new Date(message.created_at as string).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              </Stack>

              {/* {isCurrent && (
                <Flex align="center" gap={2}>
                  <MessageCenterDeleteMessage message={message} routes={routes} />
                  <MessageCenterUpdateMessage message={message} routes={routes} />
                </Flex>
              )} */}
            </Flex>
          </Box>
        </Box>

        {isCurrent && <Avatar size="sm" src={message.person_main_avatar_url} name={message.person_name} ml={3} />}
      </Flex>

      {menuPosition && (
        <Menu isOpen={menuPosition !== null} onClose={handleClose}>
          <MenuButton
            as={Box}
            position="fixed"
            top={`${menuPosition.y}px`}
            left={`${menuPosition.x}px`}
            w="0px"
            h="0px"
          />
          <MenuList>
            {isCurrent && !checkMessageTimeToUpdate() && (
              <MessageCenterDeleteMessage message={message} routes={routes} setMenuPosition={setMenuPosition} />
            )}
            {isCurrent && !checkMessageTimeToUpdate() && (
              <MessageCenterUpdateMessage message={message} routes={routes} setMenuPosition={setMenuPosition} />
            )}
            <MessageCenterCopyMessage message={message} setMenuPosition={setMenuPosition} />
          </MenuList>
        </Menu>
      )}
    </Fragment>
  )
}
